import axios from "axios";

import type {
	Artifact,
	Company,
	MemoryInsight,
	Settings,
	User,
	UserMe,
} from "@virtual-brain-lib/types";

import env from "../environments";
import { queryOptions } from "../query-client";

export const userMe = queryOptions({
	async queryFn() {
		const response = await axios.get<UserMe>(`${env.apiUrl}/users/me`);

		return response.data;
	},
	queryKey: "userMe",
	// user data does not change frequently and is used very high up the component tree.
	// Refetching it aggressively is not necessary, and triggers lot of re-renders.
	// Conclusion: staleTime is set to 5 minutes + not refetch on window refocus
	staleTime: 5 * 60 * 1000, // 5 minutes,
	refetchOnWindowFocus: false,
});

export async function updateUser(user: Partial<User>) {
	await axios.put(`${env.apiUrl}/users`, user);
}

export async function updateCompany(company: Partial<Company>) {
	await axios.put(`${env.apiUrl}/users/company`, company);
}

export async function updateSettings(settings: Settings) {
	await axios.put(`${env.apiUrl}/users/settings`, settings);
}

export const getCompanyUsers = queryOptions({
	async queryFn() {
		const response = await axios.get<User[]>(`${env.apiUrl}/users/company`);

		return response.data;
	},
	queryKey: "companyUsers",
});

export async function getArtifacts() {
	const response = await axios.get<Artifact[]>(`${env.apiUrl}/users/artifacts`);

	return response.data;
}

export const getMemoryInsights = queryOptions({
	async queryFn() {
		const response = await axios.get<MemoryInsight[]>(`${env.apiUrl}/users/memoryinsights`);

		return response.data;
	},
	queryKey: "memoryInsights",
});

export async function createAutomaticMemoryInsight({
	chatId,
	query,
}: {
	chatId: string;
	query: string;
}) {
	const response = await axios.post<MemoryInsight[]>(
		`${env.apiUrl}/users/memoryinsights/automatic`,
		{
			chatId,
			query,
		},
	);

	return response.data;
}

export async function createMemoryInsight(memoryInsight: Partial<MemoryInsight>) {
	const response = await axios.post<MemoryInsight>(`${env.apiUrl}/users/memoryinsights`, {
		title: memoryInsight.title,
		content: memoryInsight.content,
	});

	return response.data;
}

export async function updateMemoryInsight(memoryInsight: Partial<MemoryInsight> & { id: string }) {
	const response = await axios.put<MemoryInsight>(
		`${env.apiUrl}/users/memoryinsights/${memoryInsight.id}`,
		{
			title: memoryInsight.title,
			content: memoryInsight.content,
		},
	);

	return response.data;
}

export async function deleteMemoryInsight(id: string) {
	const response = await axios.delete<MemoryInsight>(`${env.apiUrl}/users/memoryinsights/${id}`);

	return response.data;
}

export const getAdminCompanyUsers = queryOptions({
	async queryFn({ queryKey }) {
		const [, params] = queryKey;

		const response = await axios.get<User[]>(`${env.apiUrl}/users/company`, { params });

		return response.data;
	},
	queryKey: "users",
});

export const blockUser = async ({ userId }: { userId: string }) => {
	await axios.put(`${env.apiUrl}/users/${userId}/block`);
};

export const unblockUser = async ({ userId }: { userId: string }) => {
	await axios.put(`${env.apiUrl}/users/${userId}/unblock`);
};

export const inviteUsers = async ({ emails }: { emails: string[] }) => {
	await axios.post(`${env.apiUrl}/users/invite`, { emails });
};

export const getCompanyAdmin = async ({ companyId }: { companyId: string }) => {
	const response = await axios.get<User[]>(`${env.apiUrl}/users/company/admin`, {
		params: { companyId },
	});

	return response.data;
};

export const deleteUser = async ({ userId }: { userId: string }) => {
	await axios.delete(`${env.apiUrl}/users/${userId}`);
};
